/**
 * analytics.js
 *
 * Thin wrapper around Google Analytics 4 (gtag). All tracking flows
 * through this module so we have one place to change measurement IDs,
 * suppress events in dev, or swap to a different platform later.
 *
 * Privacy posture (configured in public/index.html):
 *   - Consent Mode v2: ad_storage / ad_user_data / ad_personalization DENIED by default
 *   - Google Signals disabled
 *   - Ad personalization signals disabled
 *   - IP anonymisation on
 *   - Auto page_view OFF (this module handles SPA route changes)
 *
 * In production we collect analytics events with no advertising cookies
 * and no remarketing data. That's the trade-off we accept for not
 * showing a cookie banner.
 */

export const GA_MEASUREMENT_ID = 'G-22ZBTMJVM1'

const isProduction = () =>
  typeof window !== 'undefined' &&
  window.location &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1'

const isGtagAvailable = () =>
  typeof window !== 'undefined' && typeof window.gtag === 'function'

const safeGtag = (...args) => {
  if (!isGtagAvailable()) return
  if (!isProduction()) {
    // Helpful in dev — visible in console without polluting GA
    // eslint-disable-next-line no-console
    console.debug('[analytics:dev]', ...args)
    return
  }
  try {
    window.gtag(...args)
  } catch (err) {
    // Never let analytics break the page
    // eslint-disable-next-line no-console
    console.warn('analytics error', err)
  }
}

/**
 * Send a page view. Called on every React Router route change.
 */
export const trackPageView = (path, title) => {
  safeGtag('event', 'page_view', {
    page_path: path,
    page_title: title || (typeof document !== 'undefined' ? document.title : ''),
    page_location:
      typeof window !== 'undefined' && window.location
        ? window.location.href
        : path,
  })
}

/**
 * Generic GA4 event.
 */
export const trackEvent = (name, params = {}) => {
  safeGtag('event', name, params)
}

/**
 * A meaningful CTA click — primary action buttons (Get in touch,
 * Start a conversation, Start a project, Work with us).
 */
export const trackCtaClick = (label, location) => {
  trackEvent('cta_click', {
    cta_label: label,
    cta_location: location || (typeof window !== 'undefined' ? window.location.pathname : ''),
  })
}

/**
 * Outbound link click — press references, social, partner sites.
 */
export const trackOutboundClick = (url, label, location) => {
  trackEvent('outbound_click', {
    link_url: url,
    link_label: label,
    link_location:
      location || (typeof window !== 'undefined' ? window.location.pathname : ''),
  })
}

/**
 * Contact form submission success. GA4's recommended conversion event
 * for B2B sites is `generate_lead`.
 */
export const trackLead = (params = {}) => {
  trackEvent('generate_lead', {
    form_name: 'contact',
    ...params,
  })
}
